import { CdkDrag, CdkDragDrop, CdkDragEnter, CdkDropList } from '@angular/cdk/drag-drop';
import { Component, ContentChild, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { ListLayoutType, LibraryType } from '../../../models';
import { LibraryViewModel } from '../../models';

@Component({
    selector: 'lib-library-list',
    templateUrl: './library-list.component.html',
    styleUrls: ['./library-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LibraryListComponent implements OnInit, OnChanges {
  @Input() public readonly placeholders = new Array(8).fill(null);
  @Input() public enablePinned = false;
  @Input() layout: ListLayoutType = ListLayoutType.GRID;
  @Input() items: LibraryViewModel[] = [];
  @Input() disabled = false;
  @Input() loading = false;
  @Input() defaultSort: string;
  @Input() viewDetailsButton = false;
  @Input() notifyButtonEnabled = false;
  @Input() dropListConnectedTo: string[];
  @Input() canEditAndDelete = false;
  @Input() canReorder = false;

  @ContentChild('tableActionsTemplate') tableActionsTemplate?: TemplateRef<any>;

  @Output() openDetails: EventEmitter<LibraryViewModel> = new EventEmitter();
  @Output() pinnedChanged: EventEmitter<LibraryViewModel> = new EventEmitter();
  @Output() open: EventEmitter<LibraryViewModel> = new EventEmitter();
  @Output() download: EventEmitter<LibraryViewModel> = new EventEmitter();
  @Output() edit: EventEmitter<LibraryViewModel> = new EventEmitter();
  @Output() notifyUsers: EventEmitter<LibraryViewModel> = new EventEmitter();
  @Output() delete: EventEmitter<LibraryViewModel> = new EventEmitter();
  @Output() sortChanged: EventEmitter<Sort> = new EventEmitter();
  @Output() moveFileInsideFolder: EventEmitter<{ folder: LibraryViewModel, item: LibraryViewModel }> = new EventEmitter();
  @Output() reorder: EventEmitter<{ fromIndex: number, toIndex: number }> = new EventEmitter();

  public readonly layoutTypes: typeof ListLayoutType = ListLayoutType;
  public readonly libraryTypes: typeof LibraryType = LibraryType;

  public contextButtonEnabled = false;
  public contextMenuItemsEnabled: Record<string, boolean> = {};
  public folderReorder = false;
  public swiperOptions = {
    breakpoints: {
      0: {
        slidesPerView: 1,
        showOverflow: true,
        reducedWidth: true,
        navigation: {
          enabled: false
        }
      },
      480: {
        slidesPerView: 'auto',
        showOverflow: false,
        reducedWidth: false,
        navigation: {
          enabled: false
        }
      },
      800: {
        slidesPerView: 3,
        showOverflow: false,
        reducedWidth: false,
        navigation: {
          enabled: true,
          wide: true
        },
      },
      1440: {
        slidesPerView: 4,
        showOverflow: false,
        reducedWidth: false,
        navigation: {
          enabled: true,
          wide: true
        },
      }
    }
  }

  public canDrop = (drag: CdkDrag, drop: CdkDropList) => {
    const dragItemItem = drag.data?.type;
    const dropItemItem = drop.data?.type;

    if (dragItemItem === LibraryType.FOLDER && dropItemItem === LibraryType.FILE) {
      return false;
    }

    return true;
  }

  private isDragging = false;
  private folderDragOverTimeout;
  private readonly FOLDER_REORDER_TIME_MS = 750;

  constructor() { }

  public ngOnInit(): void {
    this.updateContextMenuItemsEnabled();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { items, viewDetailsButton, canEditAndDelete, enablePinned, notifyButtonEnabled} = changes;

    if (!items && !viewDetailsButton && !canEditAndDelete && !enablePinned && !notifyButtonEnabled) {
      return;
    }

    this.updateContextMenuItemsEnabled();
  }

  public tableSortChanged(sort: Sort): void {
    this.sortChanged.emit(sort);
  }

  private updateContextMenuItemsEnabled(): void {
    const contextMenuItemsEnabled: Record<string, boolean> = {};
    let contextButtonEnabled = false;

    for (const item of this.items) {
      const hasContextMenuItems = this.hasContextMenuItems(item);

      contextMenuItemsEnabled[item.id] = hasContextMenuItems;
      contextButtonEnabled = contextButtonEnabled || hasContextMenuItems;
    }

    this.contextMenuItemsEnabled = contextMenuItemsEnabled;
    this.contextButtonEnabled = contextButtonEnabled;
  }

  private hasContextMenuItems(item: LibraryViewModel): boolean {
    if (!item) {
      return false;
    }

    const operations = item._meta?.operations;

    return !!(this.viewDetailsButton
      || (item.canDownload && item.type !== LibraryType.FOLDER)
      || (this.canEditAndDelete && (operations?.edit || operations?.delete))
      || this.enablePinned
      || (this.notifyButtonEnabled && operations?.sendNotification));
  }

  public drop(event: CdkDragDrop<any>, table = false, folderReorder = false): void {
    const currentLibraryItem = table ? event.container.data[event.currentIndex] : event.container.data;
    const previousLibraryItem = table ? event.item.data : event.previousContainer.data;
    const currentIndex = table ? event.currentIndex : this.items.findIndex(item => currentLibraryItem.id === item.id);
    const previousIndex = table ? event.previousIndex : this.items.findIndex(item => previousLibraryItem.id === item.id);

    if (currentLibraryItem?.id === previousLibraryItem?.id) {
      return;
    }

    if (currentLibraryItem?.type === LibraryType.FOLDER && !folderReorder) {
      this.moveFileInsideFolder.emit({ folder: currentLibraryItem, item: previousLibraryItem});
      return;
    }

    this.reorder.emit({fromIndex: previousIndex, toIndex: currentIndex});
  }

  public onOpen(item: LibraryViewModel): void {
    if (this.isDragging) {
      return;
    }

    this.open.emit(item);
  }

  public onDragStarted(): void {
    this.isDragging = true;
  }

  public onDragEnded(): void {
    setTimeout(() => {
      this.clearFolderDragOverTimeout();
      this.isDragging = false;
    });
  }

  public onDragEntered(drag: CdkDragEnter<LibraryViewModel>): void {
    this.clearFolderDragOverTimeout();

    if (drag.item.data?.type !== LibraryType.FOLDER) {
      return;
    }

    this.folderReorder = true;
    this.folderDragOverTimeout = setTimeout(() => {
      this.folderReorder = false;
    }, this.FOLDER_REORDER_TIME_MS);
  }

  private clearFolderDragOverTimeout(): void {
    clearTimeout(this.folderDragOverTimeout);
    this.folderReorder = false;
  }

}
